import { useMutation } from '@apollo/client';
import { GraphQLErrorOptions } from 'graphql';
import { useInviteContext } from '../../contexts/InviteContext/useInviteContext';
import { AlreadyExistsError, ChannelFiltersError } from 'activate-errors';
import { joinChannel } from '../../graphql/mutation';
import { updateUser } from '../../graphql/user';
import { isThisError } from '../../helpers';
import { PublicProfileData } from './types';
import Storage, { StorageKeys } from '../../helpers/Storage';
import { useWorkplaceOnboardingEnabled } from './useWorkplaceOnboardingEnabled';
import { Channel, OnboardingData, UpdateUserData } from 'application-types';
import useUserLoginsQuery from '../useUserLoginsQuery';
import {
  UserLoginData,
  UserLoginStatusEnum,
  UserLoginTypeEnum,
} from 'constants-user';

interface UserNotDefinedInterface {
  name: string;
  message: string;
}

export class UserNotDefined extends Error implements UserNotDefinedInterface {
  constructor(message?: string) {
    super(message);
    this.name = 'UserNotDefined';
  }
}

export function useOnboardUser(
  userId?: string,
  publicProfileData?: PublicProfileData
): {
  onboardUser: (accountData: OnboardingData) => Promise<void>;
} {
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();
  const [updateUserMutation] = useMutation(updateUser);
  const [joinChannelMutation] = useMutation(joinChannel);
  const [userLogins] = useUserLoginsQuery();

  const {
    inviteId,
    clearInvite,
    claimInvite,
    building: invitedBuilding,
    company: invitedCompany,
  } = useInviteContext();

  const hasUserAlreadyJoinedTheChannel = (err: any) => {
    const isApolloCodedError = err.graphQLErrors?.some(
      (graphQLError: GraphQLErrorOptions) =>
        isThisError(graphQLError?.extensions?.exception, AlreadyExistsError)
    );

    return isApolloCodedError;
  };

  const isChannelFiltersError = (err: any) => {
    const isApolloCodedError = err.graphQLErrors?.some(
      (graphQLError: GraphQLErrorOptions) =>
        isThisError(graphQLError?.extensions?.exception, ChannelFiltersError)
    );

    return isApolloCodedError;
  };

  const handleJoinChannelError = (err: any) => {
    const alreadyJoined = hasUserAlreadyJoinedTheChannel(err);

    if (!alreadyJoined) {
      const isInvitedUser = Boolean(inviteId);
      const skipFilterError = isWorkplaceOnboardingEnabled && isInvitedUser;

      // This allows invited users to complete onboarding even if their email domain
      // doesn't match the channel's email filters
      if (isChannelFiltersError(err) && skipFilterError) {
        return;
      }

      throw err;
    }
  };

  const joinChannelWithInvite = async (
    shouldClaimInvite: boolean,
    channel?: Channel,
    invitedChannel?: Channel
  ) => {
    if (inviteId && channel && invitedChannel && shouldClaimInvite) {
      await claimInvite(inviteId, channel._id);
      clearInvite();
    } else {
      if (!channel) {
        return;
      }

      try {
        await joinChannelMutation({
          variables: {
            channelId: channel._id,
          },
        });
      } catch (err) {
        handleJoinChannelError(err);
      }
    }
  };

  const getPrimaryChannelId = (building?: Channel, company?: Channel) => {
    let primaryChannelId: string | undefined = building?._id;

    const isInvitedToCompanyOnly = !invitedBuilding && invitedCompany;

    if (
      (isInvitedToCompanyOnly || publicProfileData?.isCompany) &&
      company?._id
    ) {
      primaryChannelId = company._id;
    }

    return primaryChannelId;
  };

  const updateUserAndJoinChannels = async ({
    fullName,
    building,
    company,
    marketingOptIn,
    phone,
    location,
  }: OnboardingData) => {
    if (!userId) {
      throw new UserNotDefined();
    }

    const updateUserData: UpdateUserData = {
      _id: userId,
      profile: {
        name: fullName,
      },
      settings: {
        emailOptIn: marketingOptIn,
      },
    };

    if (phone) {
      updateUserData.profile.phone = phone;

      const phoneLoginExists =
        userLogins?.some(
          login => login.type === UserLoginTypeEnum.Phone && login.key === phone
        ) ?? false;

      if (!phoneLoginExists) {
        const phoneLogin: UserLoginData = {
          type: UserLoginTypeEnum.Phone,
          key: phone,
          isPrimary: false,
          status: UserLoginStatusEnum.Verified,
        };

        updateUserData.logins = [phoneLogin];
      }
    }

    if (location) {
      updateUserData.settings.location = location;
    }

    await updateUserMutation({
      variables: {
        user: updateUserData,
      },
    });

    // If invited to building WITH company, claim invite once (handles both channels)
    const isInvitedToBuildingWithCompany = invitedBuilding && invitedCompany;

    if (isInvitedToBuildingWithCompany && inviteId && building) {
      await claimInvite(inviteId, building._id);
      clearInvite();
    } else {
      // For each channel: use claimInvite if invited, otherwise use joinChannel
      if (building) {
        await joinChannelWithInvite(
          !!invitedBuilding,
          building,
          invitedBuilding
        );
      }

      if (company) {
        await joinChannelWithInvite(!!invitedCompany, company, invitedCompany);
      }
    }

    const primaryChannelId = getPrimaryChannelId(building, company);

    if (isWorkplaceOnboardingEnabled && primaryChannelId) {
      await Storage.setItem(StorageKeys.PRIMARY_CHANNEL, primaryChannelId);
    }

    await Storage.removeItem(StorageKeys.ONBOARDING_CHANNEL_ID);
  };

  return { onboardUser: updateUserAndJoinChannels };
}
